import { z } from 'zod';
import { REFERRAL_CONFIG } from '@hero/core';
import { dateSchema, objectIdSchema } from '../../validation';
import { referralCodeSchema } from './referral.code.schema';

/**
 * How a Lead gets linked to a `ReferralLink`.
 *
 * - `direct` — the code travelled with the lead: `?r=CODE` in the URL,
 *   `r=CODE` / the bare code in a first WhatsApp message, `referralCode`
 *   on a signup payload. Certain.
 * - `closest_tracking` — no code arrived with the lead, but a
 *   `ReferralTracking` hit for this link landed within
 *   `attribution.windowMinutes` before the lead was created (someone
 *   clicked the partner's WhatsApp button at 12:50 and messaged us at
 *   1:00). Probabilistic; the admin panel shows it as "matched by time"
 *   and lets a human relink or unlink.
 * - `manual` — an admin linked the lead from the CRM.
 *
 * A `direct` match always beats a `closest_tracking` one, and the
 * evaluator never downgrades: once a lead carries a `direct` attribution
 * a later time-based match is ignored.
 */
export const referralAttributionModeSchema = z.enum(['direct', 'closest_tracking', 'manual']);
export type ReferralAttributionMode = z.infer<typeof referralAttributionModeSchema>;

/**
 * Per-link matching config, set when the link is created.
 *
 * `modes` is a set rather than one value so a WhatsApp-first link can
 * accept both a code typed into the message AND a time-based match when
 * the prospect typed nothing, while a strict paid-campaign link can
 * insist on `direct` only.
 */
export const referralLinkAttributionSchema = z.object({
  /**
   * Which matching strategies this link accepts. `manual` is always
   * allowed and is not listed here.
   */
  modes: z
    .array(z.enum(['direct', 'closest_tracking']))
    .min(1)
    .default(['direct']),
  /**
   * For `closest_tracking`: how far back from the lead's creation a hit
   * on this link may sit and still count. Stored in minutes; the admin
   * form shows hours. Defaults to 72h (3 days). The evaluator picks the
   * latest qualifying hit across every link that allows time matching,
   * so a shorter window makes a link less greedy.
   */
  windowMinutes: z.coerce
    .number()
    .int()
    .min(1)
    .max(REFERRAL_CONFIG.maxAttributionWindowMinutes)
    .default(REFERRAL_CONFIG.defaultAttributionWindowMinutes),
});
export type ReferralLinkAttribution = z.infer<typeof referralLinkAttributionSchema>;

/**
 * The attribution stamp written on the referred entity — the Lead
 * (`meta.sourceMeta.referral`), and copied to the Org / User the lead
 * converts into (`meta.referral`) so the reward evaluator can resolve a
 * paid Billing back to its partner without going through the Lead.
 *
 * `code` and `linkId` / `partnerId` are all stored: the ids are what the
 * evaluator joins on, the code is what a human recognises in the CRM
 * and what stays readable if a link is ever deleted.
 */
export const referralAttributionSchema = z.object({
  /** `ReferralPartner._id`. */
  partnerId: objectIdSchema,
  /** `ReferralLink._id`. */
  linkId: objectIdSchema,
  /**
   * The link's public code as it was at attribution time — the link the
   * person **converted through** (the `?r=` active when they signed up
   * or wrote to us). This is the link that owns the person and the
   * reward.
   */
  code: referralCodeSchema,
  /**
   * The first code the same browser / device ever saw (`hero_r`
   * first-touch cookie), when it differs from `code`. History only —
   * "came in through Khaled, signed up through Mohammed". Never used for
   * ownership or rewards.
   */
  originalCode: referralCodeSchema.optional(),
  /** How the match was made. */
  mode: referralAttributionModeSchema,
  /** When the attribution was written. */
  at: dateSchema,
  /**
   * For `closest_tracking`: the `ReferralTracking._id` that was matched,
   * so the admin panel can show "matched to a click at 12:50 from
   * wathiqaplus.com".
   */
  trackingId: objectIdSchema.optional(),
  /**
   * The visitor id the tracking hit carried, when known. Lets the
   * evaluator recognise the same browser again across a form submit and
   * a later signup.
   */
  visitorId: z.string().trim().optional(),
  /**
   * PostHog `distinct_id` of the anonymous web session behind the
   * matched hit (`ReferralTracking.context.posthogDistinctId`). The
   * backend aliases it to the person once one is known (WhatsApp
   * phone, `User._id`) so pre-signup page views land on the same
   * PostHog Person as post-signup activity.
   */
  posthogDistinctId: z.string().trim().max(200).optional(),
});
export type ReferralAttribution = z.infer<typeof referralAttributionSchema>;
